import { EyeIcon, PencilIcon } from "@heroicons/react/outline";
import { useTranslation } from "@refinedev/core";
import Button from "components/button";
import { SwitchButton } from "components/switchButton";
import { Tag } from "components/tag";
import {
  ApplyToProductType,
  DiscountType,
  IPromotionCouponItem,
  UserType,
} from "interfaces";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { Link } from "react-router-dom";
import {
  canAccessByType,
  formatNumber,
  getApplyToProductTypeLabel,
  getFormattedDateTime,
  isHaveActionPermission,
} from "utills/helpers";

interface PromotionCouponCardProps {
  data?: IPromotionCouponItem[];
  pageSize: number;
  currentPage: number;
  _toggleActive: (id: string, isActive: boolean, conditionName: string) => void;
  _showInfoModal: (data: IPromotionCouponItem) => void;
}

export const PromotionCouponCard = ({
  data = [],
  pageSize = 10,
  currentPage = 1,
  _toggleActive,
  _showInfoModal,
}: PromotionCouponCardProps) => {
  const { translate } = useTranslation();
  return (
    <ul className="bg-white">
      {data.map((item: IPromotionCouponItem, index: number) => (
        <div
          key={index}
          className="flex flex-col gap-y-4 overflow-hidden shadow-md ring-1 ring-gray-900/5 [&:not(:last-child)]:mb-4 p-4 rounded-xl"
        >
          <div className="w-full flex items-center justify-between">
            {isHaveActionPermission("coupon", "promotion", "edit") ? (
              <SwitchButton
                checked={item.is_active}
                onChange={() =>
                  _toggleActive(item.id, !item.is_active, item.code)
                }
              />
            ) : (
              <div />
            )}
            <div className="flex flex-row items-center gap-x-2">
              <Link to={`/promotion/coupon/${item.id}`} className="w-fit">
                <span className="sr-only">View Condition</span>
                <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                  <EyeIcon className="w-4 h-4 text-black" />
                </Button>
              </Link>
              {isHaveActionPermission("coupon", "promotion", "edit") ? (
                <Link
                  to={`/promotion/coupon/${item.id}/edit`}
                  className="w-fit"
                >
                  <span className="sr-only">Edit Condition</span>
                  <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                    <PencilIcon className="w-4 h-4 text-black" />
                  </Button>
                </Link>
              ) : (
                <></>
              )}
            </div>
          </div>

          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              {translate("no")}
            </p>
            <p className="flex-1 text-right text-sm leading-6">
              {(currentPage - 1) * pageSize + 1 + index}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              {translate("coupon-code")}
            </p>
            <p className="flex-1 text-right text-sm leading-6 font-medium">
              {item?.code}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              {translate("condition-name")}
            </p>
            <div className="flex-1 text-right text-sm leading-6">
              <Link
                to={`/promotion/condition/${item.rule_id}`}
                className="whitespace-pre-wrap max-w-[280px] text-brand-500 font-medium hover:underline"
              >
                <p>{item.note}</p>
              </Link>
            </div>
          </li>
          {canAccessByType(UserType.ADMIN) && (
            <li className="relative flex items-start justify-between gap-x-6">
              <p className="flex-1 text-sm font-semibold leading-6">
                Apply For
              </p>
              <div className="flex-1 text-right text-sm leading-6">
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Tag
                    text={getApplyToProductTypeLabel(
                      item.apply_to_product_type
                    )}
                  />
                  {!isEmpty(item?.event_ids) &&
                  item?.apply_to_product_type !== ApplyToProductType.ALL ? (
                    <div
                      className="hover:cursor-pointer"
                      onClick={() => _showInfoModal(item)}
                    >
                      <Tag
                        text={`${(item.event_ids ?? []).length} event(s)`}
                        customClassName="bg-gray-100 text-gray-500 underline"
                      />
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </li>
          )}
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              {translate("discount-condition")}
            </p>
            <div className="flex-1 text-right text-sm leading-6">
              {item?.discount_type === DiscountType.PERCENT ? (
                item?.discount_value ? (
                  <p>{formatNumber(item?.discount_value ?? 0)}%</p>
                ) : (
                  <p className="text-gray-400">N/A</p>
                )
              ) : item?.discount_type === DiscountType.FIX_PRICE ? (
                <>
                  {item?.discount_value || item?.discount_value_usd ? (
                    <p>
                      {item?.discount_value
                        ? `${formatNumber(item?.discount_value)}đ`
                        : ""}{" "}
                      {item?.discount_value && item?.discount_value_usd
                        ? "|"
                        : ""}{" "}
                      {item?.discount_value_usd
                        ? `$${formatNumber(item?.discount_value_usd)}`
                        : ""}
                    </p>
                  ) : (
                    <p className="text-gray-400">N/A</p>
                  )}
                </>
              ) : (
                <></>
              )}
            </div>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              {translate("used-count-per-total")}
            </p>
            <p className="flex-1 text-right text-sm leading-6">
              {item.times_used}/{item.max_times_used}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              {translate("apply-from")}
            </p>
            <p className="flex-1 text-right text-sm leading-6">
              {getFormattedDateTime(item.apply_from)}
            </p>
          </li>
          <li className="relative flex items-start justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              {translate("apply-to")}
            </p>
            <p className="flex-1 text-right text-sm leading-6">
              {getFormattedDateTime(item.apply_to)}
            </p>
          </li>
        </div>
      ))}
    </ul>
  );
};
